import { isEmpty } from 'lodash';
import { Pushkin } from '@fiverr-private/pushkin_js';
import { logger } from '@fiverr-private/obs';
import { globalEventBus } from '@fiverr-private/futile';
import { getContext } from '@fiverr-private/fiverr_context';
import { PushkinClient, RealtimeEventHandler, RealtimeEventHandlerMapping, IPushkinConfig } from '../types';
import { IPushkinLoggerConfig } from '../types/PushkinConfig';
import { isClient } from './isClient';
import { pushkinMetric } from './metric';

type InitializationState = 'initializing' | 'initialized' | 'failed';

class RealtimeListener {
    private static readonly initializationEvent = `pushkin_client_initialized_${Date.now()}`;

    initializationState: InitializationState | null = null;
    pushkin: PushkinClient | null = null;
    metricBase = 'default';
    loggerConfig?: IPushkinLoggerConfig;
    logEnrichments: Record<string, unknown> = {};

    async init(pushkinConfig: IPushkinConfig): Promise<void> {
        const isClientSide = isClient();

        if (!isEmpty(this.pushkin) || this.initializationState === 'initializing' || !pushkinConfig || !isClientSide) {
            return;
        }

        const { userId, url, pageCtxId } = getContext();

        this.loggerConfig = pushkinConfig?.loggerConfig;
        this.metricBase = pushkinConfig?.statsConfig?.consumerName ?? this.metricBase;
        this.logEnrichments = {
            description: 'Failed to connect to Pushkin',
            source: pushkinConfig?.loggerConfig?.domain || 'fiverr_neo',
            url,
            pageCtxId,
            ...(userId && { userId }),
            ...(this.loggerConfig?.enrichments && { ...this.loggerConfig.enrichments }),
            ...(pushkinConfig?.statsConfig?.consumerName && {
                consumerName: pushkinConfig.statsConfig.consumerName,
            }),
        };

        try {
            this.initializationState = 'initializing';
            pushkinMetric.count(`${this.metricBase}.initializing`);

            // Start measuring time for initialization
            const startTime = Date.now();
            this.pushkin = await Pushkin.connect(pushkinConfig);
            const elapsedTime = Date.now() - startTime;

            pushkinMetric.time(`${this.metricBase}.initialization.time`, elapsedTime);

            this.initializationState = 'initialized';
            pushkinMetric.count(`${this.metricBase}.initialization`);

            globalEventBus.deferred(RealtimeListener.initializationEvent, this.pushkin);
        } catch (error: any) {
            this.initializationState = 'failed';
            pushkinMetric.count(`${this.metricBase}.failed`);
            logger.error(error, this.logEnrichments);
        }
    }

    async subscribe(channelName: string, handlerMapping: RealtimeEventHandlerMapping): Promise<void> {
        if (!this.pushkin) {
            globalEventBus.on(RealtimeListener.initializationEvent, () => {
                this.subscribe(channelName, handlerMapping);
            });

            return;
        }

        try {
            const channel = await this.pushkin.subscribe({
                channelName,
            });

            pushkinMetric.count(`${this.metricBase}.subscribe.success`);

            for (const [eventName, handler] of Object.entries<RealtimeEventHandler>(handlerMapping)) {
                channel.bind(eventName, handler);
            }
        } catch (error: any) {
            pushkinMetric.count(`${this.metricBase}.subscribe.failure`);
            logger.error(error, this.logEnrichments);
        }
    }

    async unsubscribe(channelName: string): Promise<void> {
        if (!this.pushkin) {
            logger.warn('Pushkin client is not initialized');
            pushkinMetric.count(`${this.metricBase}.unsubscribe.failure`);

            return;
        }

        try {
            await this.pushkin.unsubscribe(channelName);

            pushkinMetric.count(`${this.metricBase}.unsubscribe.success`);
        } catch (error: any) {
            pushkinMetric.count(`${this.metricBase}.unsubscribe.failure`);
            logger.error(error, this.logEnrichments);
        }
    }
}

export default new RealtimeListener();
